import { css, cx } from "@emotion/css";
import { Badge, Button, scrollbarStyles, useIsHovered, useIsMobile } from "@octopusdeploy/design-system-components";
import { ArrowLeftFromLineIcon, ArrowRightToLineIcon, PenLineIcon, UnavailableIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, letterSpacing, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DetailedDashboardProject, DetailedDashboardProjectGroup, EnvironmentWithDeployments, InitialDashboardProjectGroup, ProjectLiveStatusResource, ProjectsPageEnvironmentDeployment, ProjectsPageProject, ProjectsPageProjectGroup, TenantDeploymentProgress, ProjectGroupLiveStatusResource, } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useCollapsableColumns } from "~/areas/dashboard/DashboardOverview/useCollapsableColumns";
import { ProjectsDashboardRowHeaderContent, ProjectsDashboardRowHeaderSkeleton } from "~/areas/dashboard/ProjectsDashboard/ProjectsDashboardRowHeaderContent";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import type { FavouriteProjectsData } from "~/areas/projects/components/Projects/ProjectsDashboard";
import InternalLink from "~/components/Navigation/InternalLink";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import { DeploymentTaskStatus } from "../Deployments/DeploymentTaskStatus";
interface ProjectGroupsProps {
    projectGroups: InitialDashboardProjectGroup[] | DetailedDashboardProjectGroup[] | ProjectsPageProjectGroup[];
    projectGroupsLiveStatus: ProjectGroupLiveStatusResource[];
    showLiveStatus: boolean;
    onAddProjectToGroup: (projectGroupId: string) => void;
    favouriteProjectsData: FavouriteProjectsData;
    spaceId: string;
    projectStatus: ProjectStatus;
}
// Memoized so the heavy project-group tree is skipped when its props haven't
// changed by reference. useInlineStatusQuery preserves result references across
// polls that return deep-equal payloads, so as long as the props passed in here
// are themselves stable (see useMemo for projectGroupsLiveStatus and
// favouriteProjectsData in ProjectsDashboard.tsx), this whole subtree skips
// re-rendering on identical-payload polls. Without React.memo, the parent
// component's internal useFetchIntents state would re-render us every poll.
export const DashboardProjectGroups = React.memo(function DashboardProjectGroups({ projectGroups, favouriteProjectsData, spaceId, onAddProjectToGroup, projectStatus, projectGroupsLiveStatus, showLiveStatus }: ProjectGroupsProps) {
    return (<div className={projectGroupsStyles}>
            {projectGroups.map((group, index) => (<DashboardProjectGroup key={group.Id} projectGroup={group} favouriteProjectsData={favouriteProjectsData} spaceId={spaceId} onAddProjectToGroup={onAddProjectToGroup} projectStatus={projectStatus} showLiveStatus={showLiveStatus} projectGroupLiveStatus={projectGroupsLiveStatus.find((x) => x.Id === group.Id)}/>))}
        </div>);
});
export function ProjectGroupDeploymentsSkeleton() {
    return (<div className={projectGroupStyles}>
            <ProjectGroupHeaderSkeleton />
            <div className={cx(deploymentsTableContainerStyle, scrollbarStyles)}>
                <table aria-label="Project Group" className={tableStyle}>
                    <thead>
                        <tr className={deploymentsTableColumnHeaderStyles}/>
                    </thead>
                    <tbody>
                        {[...Array(3)].map((_item, index) => (<tr key={index}>
                                <ProjectsDashboardRowHeaderSkeleton />
                                <ProjectDeploymentRowSkeleton />
                            </tr>))}
                    </tbody>
                </table>
            </div>
        </div>);
}
const projectGroupsStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: space[24],
});
interface DashboardProjectGroupProps {
    favouriteProjectsData: FavouriteProjectsData;
    projectGroup: InitialDashboardProjectGroup | DetailedDashboardProjectGroup | ProjectsPageProjectGroup;
    onAddProjectToGroup: (projectGroupId: string) => void;
    spaceId: string;
    projectStatus: ProjectStatus;
    projectGroupLiveStatus?: ProjectGroupLiveStatusResource;
    showLiveStatus: boolean;
}
function DashboardProjectGroup({ projectGroup, favouriteProjectsData, spaceId, onAddProjectToGroup, projectStatus, showLiveStatus, projectGroupLiveStatus }: DashboardProjectGroupProps) {
    const showingPartialResults = projectGroup.Projects.length !== projectGroup.TotalProjects;
    return (<div className={projectGroupStyles}>
            <ProjectGroupHeader projectGroupId={projectGroup.Id} title={projectGroup.Name} projectCount={projectGroup.TotalProjects} spaceId={spaceId}/>
            <ProjectGroupDeploymentsTable group={projectGroup} favouriteProjectsData={favouriteProjectsData} onAddProjectToGroup={onAddProjectToGroup} projectStatus={projectStatus} showLiveStatus={showLiveStatus} projectGroupLiveStatus={projectGroupLiveStatus} spaceId={spaceId}/>
            {showingPartialResults && (<div className={continuedOverPageStyles}>
                    Showing {projectGroup.Projects.length} of {projectGroup.TotalProjects}
                </div>)}
        </div>);
}
const continuedOverPageStyles = css({
    display: "flex",
    justifyContent: "end",
    padding: `${space[6]} ${space[16]} ${space[6]} ${space[16]}`,
    font: text.body.regular.small,
    color: themeTokens.color.text.tertiary,
    backgroundColor: themeTokens.color.background.secondary.default,
});
const projectGroupStyles = css({
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.medium,
});
interface ProjectGroupHeaderProps {
    projectGroupId: string;
    title: string;
    description?: string;
    projectCount: number;
    spaceId: string;
}
function ProjectGroupHeader({ projectGroupId, title, description, projectCount, spaceId }: ProjectGroupHeaderProps) {
    const groupHeaderSectionRef = React.useRef<HTMLHeadingElement>(null);
    const isHovered = useIsHovered(groupHeaderSectionRef);
    const isMobile = useIsMobile();
    return (<div className={projectGroupHeaderStyles.root}>
            <h2 ref={groupHeaderSectionRef} className={projectGroupHeaderStyles.groupName}>
                <InternalLink className={projectGroupLinkStyles} to={links.projectsPage.generateUrl({ spaceId }, { searchValue: title })}>
                    <span>{title}</span>
                </InternalLink>
                <Badge count={projectCount} size="small" variant="neutral"/>
                <div className={cx(hideEditIconContainerStyles, { [showEditIconContainerStyles]: isMobile || isHovered })}>
                    <InternalLink to={links.projectGroupPage.generateUrl({ spaceId, projectGroupId })}>
                        <PenLineIcon size={20}/>
                    </InternalLink>
                </div>
            </h2>
            {description && <p>{description}</p>}
        </div>);
}
const hideEditIconContainerStyles = css({
    display: "none",
});
const showEditIconContainerStyles = css({
    display: "block",
});
function ProjectGroupHeaderSkeleton() {
    return (<div className={projectGroupHeaderStyles.root}>
            <div className={projectGroupHeaderStyles.skeleton}>
                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
            </div>
        </div>);
}
const projectGroupHeaderStyles = {
    root: css({
        display: "flex",
        flexDirection: "column",
        padding: `${space[16]} ${space[16]} ${space[12]} ${space[16]}`,
        borderBottom: `1px solid ${themeTokens.color.border.primary}`,
    }),
    groupName: css({
        all: "unset",
        font: text.body.bold.large,
        letterSpacing: letterSpacing.wider,
        display: "flex",
        gap: space[8],
        alignItems: "center",
    }),
    skeleton: css({
        width: "16.25rem",
        height: "1.5rem",
    }),
};
interface ProjectGroupDeploymentsTableProps {
    favouriteProjectsData: FavouriteProjectsData;
    onAddProjectToGroup: (projectGroupId: string) => void;
    group: InitialDashboardProjectGroup | DetailedDashboardProjectGroup | ProjectsPageProjectGroup;
    projectStatus: ProjectStatus;
    showLiveStatus: boolean;
    projectGroupLiveStatus?: ProjectGroupLiveStatusResource;
    spaceId: string;
}
function isDetailedDashboardProjectGroup(group: InitialDashboardProjectGroup | DetailedDashboardProjectGroup | ProjectsPageProjectGroup): group is DetailedDashboardProjectGroup | ProjectsPageProjectGroup {
    return group && "Environments" in group && Boolean(group.Environments);
}
function ProjectGroupDeploymentsTable({ spaceId, group, favouriteProjectsData, onAddProjectToGroup, projectStatus, showLiveStatus, projectGroupLiveStatus }: ProjectGroupDeploymentsTableProps) {
    return isDetailedDashboardProjectGroup(group) ? (<ProjectGroupDetailedDeploymentsTable group={group} favouriteProjectsData={favouriteProjectsData} onAddProjectToGroup={onAddProjectToGroup} projectStatus={projectStatus} showLiveStatus={showLiveStatus} projectGroupLiveStatus={projectGroupLiveStatus} spaceId={spaceId}/>) : (<ProjectGroupInitialDeploymentsTable favouriteProjectsData={favouriteProjectsData} group={group} onAddProjectToGroup={onAddProjectToGroup} projectStatus={projectStatus}/>);
}
interface ProjectGroupDetailedDeploymentsTableProps {
    favouriteProjectsData: FavouriteProjectsData;
    onAddProjectToGroup: (projectGroupId: string) => void;
    group: DetailedDashboardProjectGroup | ProjectsPageProjectGroup;
    projectStatus: ProjectStatus;
    showLiveStatus: boolean;
    projectGroupLiveStatus?: ProjectGroupLiveStatusResource;
    spaceId: string;
}
function useScrollSync() {
    const topRef = React.useRef<HTMLDivElement>(null);
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const tableRef = React.useRef<HTMLTableElement>(null);
    const [scrollWidth, setScrollWidth] = React.useState(0);
    React.useEffect(() => {
        const table = tableRef.current;
        const container = bottomRef.current;
        if (!table || !container)
            return;
        const observer = new ResizeObserver(() => setScrollWidth(container.scrollWidth));
        observer.observe(table);
        return () => observer.disconnect();
    }, []);
    const onTopScroll = () => {
        if (bottomRef.current && topRef.current) {
            bottomRef.current.scrollLeft = topRef.current.scrollLeft;
        }
    };
    const onBottomScroll = () => {
        if (topRef.current && bottomRef.current) {
            topRef.current.scrollLeft = bottomRef.current.scrollLeft;
        }
    };
    return { topRef, bottomRef, tableRef, scrollWidth, onTopScroll, onBottomScroll };
}
const ENV_PAGE_SIZE = 20;
function ProjectGroupDetailedDeploymentsTable({ spaceId, group, favouriteProjectsData, onAddProjectToGroup, projectStatus, showLiveStatus, projectGroupLiveStatus }: ProjectGroupDetailedDeploymentsTableProps) {
    const { isColumnCollapsed, collapseColumn } = useCollapsableColumns();
    const [visibleEnvCount, setVisibleEnvCount] = React.useState(ENV_PAGE_SIZE);
    const { topRef, bottomRef, tableRef, scrollWidth, onTopScroll, onBottomScroll } = useScrollSync();
    const collapseAllEnvironmentHeader = React.useMemo(() => {
        const hasAllCollapsed = group.Environments.every((x) => isColumnCollapsed(`${group.Id}-${x.Id}`) === true);
        const allEnvironmentsKey = "{{ALL}}";
        return (<AllEnvironmentsColumnHeader key={allEnvironmentsKey} name={hasAllCollapsed ? "Expand All" : "Collapse All"} isCollapsed={hasAllCollapsed} toggleCollapse={() => {
                group.Environments.map((x) => {
                    collapseColumn(`${group.Id}-${x.Id}`, () => !hasAllCollapsed);
                });
            }}/>);
    }, [collapseColumn, group.Environments, group.Id, isColumnCollapsed]);
    const onLoadMore = React.useCallback(() => setVisibleEnvCount((c) => c + ENV_PAGE_SIZE), []);
    if (group.TotalProjects === 0) {
        return <EmptyProjectGroup onAddProjectToGroup={() => onAddProjectToGroup(group.Id)}/>;
    }
    const visibleGroupEnvironments = group.Environments.slice(0, visibleEnvCount);
    const hasMoreEnvironments = group.Environments.length > visibleEnvCount;
    const remainingCount = Math.min(ENV_PAGE_SIZE, group.Environments.length - visibleEnvCount);
    return (<>
            {hasMoreEnvironments && (<div className={environmentCountIndicatorStyles}>
                    Showing {visibleEnvCount} of {group.Environments.length} environments
                </div>)}
            {group.Projects.length > 10 && (<div ref={topRef} onScroll={onTopScroll} className={cx(topScrollbarMirrorStyles, scrollbarStyles)}>
                    <div style={{ width: scrollWidth, height: 1 }}/>
                </div>)}
            <div ref={bottomRef} onScroll={onBottomScroll} className={cx(deploymentsTableContainerStyle, scrollbarStyles)}>
                <table ref={tableRef} aria-label={group.Name} className={tableStyle}>
                    <thead>
                        <tr>
                            <th scope={"col"} className={cx(tableCellStyles, tableCellExpandedStyles, stickyColumn)}>
                                {collapseAllEnvironmentHeader}
                            </th>
                            {visibleGroupEnvironments.map((env) => (<EnvironmentColumnHeader key={env.Id} name={env.Name} isCollapsed={isColumnCollapsed(`${group.Id}-${env.Id}`)} toggleCollapse={() => collapseColumn(`${group.Id}-${env.Id}`, (prev) => !prev)}/>))}
                            {hasMoreEnvironments && <th className={cx(tableCellStyles, loadMoreColumnHeaderStyles)}/>}
                        </tr>
                    </thead>
                    <tbody>
                        {group.Projects.map((project, index, arr) => (<ProjectGroupRow key={`deploymentRow${project.Id}`} project={project} groupId={group.Id} favouriteProjectsData={favouriteProjectsData} projectStatus={projectStatus} spaceId={spaceId} showLiveStatus={showLiveStatus} projectLiveStatus={projectGroupLiveStatus?.Projects.find((x) => x.Id === project.Id)} isLastRow={index === arr.length - 1} isFirstRow={index === 0} isColumnCollapsed={isColumnCollapsed} visibleEnvCount={visibleEnvCount} hasMoreEnvironments={hasMoreEnvironments} remainingCount={remainingCount} onLoadMore={onLoadMore}/>))}
                    </tbody>
                </table>
            </div>
        </>);
}
interface ProjectGroupRowProps {
    project: DetailedDashboardProject | ProjectsPageProject;
    groupId: string;
    favouriteProjectsData: FavouriteProjectsData;
    projectStatus: ProjectStatus;
    spaceId: string;
    showLiveStatus: boolean;
    projectLiveStatus: ProjectLiveStatusResource | undefined;
    isLastRow: boolean;
    isFirstRow: boolean;
    isColumnCollapsed: (id: string) => boolean;
    visibleEnvCount: number;
    hasMoreEnvironments: boolean;
    remainingCount: number;
    onLoadMore: () => void;
}
// Memoized so polling refreshes (and any other parent re-render where a row's
// data hasn't changed) don't reconcile this row's row-header and env-cells.
// All the props are either primitives or references that are kept stable by
// the dashboard query's deep-equality short-circuit.
const ProjectGroupRow = React.memo(function ProjectGroupRow({ project, groupId, favouriteProjectsData, projectStatus, spaceId, showLiveStatus, projectLiveStatus, isLastRow, isFirstRow, isColumnCollapsed, visibleEnvCount, hasMoreEnvironments, remainingCount, onLoadMore, }: ProjectGroupRowProps) {
    const visibleProjectEnvironments = project.Environments.slice(0, visibleEnvCount);
    return (<tr className={cx({ [disabledProjectRowStyles]: project.IsDisabled })}>
            <th scope={"row"} className={cx(tableCellStyles, tableCellExpandedStyles, stickyColumn, { [lastRowHeaderStyle]: isLastRow })}>
                <ProjectsDashboardRowHeaderContent favouriteProjectsData={favouriteProjectsData} project={project} projectStatus={projectStatus}/>
            </th>
            {visibleProjectEnvironments.map((environment) => {
            const latestDeployment = environment.Deployment;
            const isCollapsed = isColumnCollapsed(`${groupId}-${environment.Id}`);
            const tenantProgress = latestDeployment?.TenantId ? getDeploymentTenantProgress(environment) || "tenanted" : "hidden";
            if (!latestDeployment) {
                return (<DeploymentStatusCell isCollapsed={isCollapsed} key={environment.Id}>
                            <NoData isCollapsed={isCollapsed}/>
                        </DeploymentStatusCell>);
            }
            const environmentLiveStatus = showLiveStatus ? projectLiveStatus?.Environments.find((x) => x.Id === environment.Id) : undefined;
            return (<DeploymentStatusCell isCollapsed={isCollapsed} key={latestDeployment.DeploymentId}>
                        <DeploymentTaskStatus spaceId={spaceId} projectSlug={project.Slug} deployment={latestDeployment} isCollapsed={isCollapsed} tenantProgress={tenantProgress} liveStatus={environmentLiveStatus}/>
                    </DeploymentStatusCell>);
        })}
            {hasMoreEnvironments && (<td className={tableCellStyles}>
                    {isFirstRow && (<div className={deploymentCellStyles}>
                            <Button label={`Load ${remainingCount} more`} onClick={onLoadMore} importance={"secondary"}/>
                        </div>)}
                </td>)}
        </tr>);
});
interface EmptyProjectGroupProps {
    onAddProjectToGroup: () => void;
}
function EmptyProjectGroup({ onAddProjectToGroup }: EmptyProjectGroupProps) {
    return (<div className={emptyProjectGroupStyles.root}>
            <div className={emptyProjectGroupStyles.callToAction}>
                <Button label={"Add Project"} onClick={onAddProjectToGroup} importance={"secondary"}/>
            </div>
        </div>);
}
const emptyProjectGroupStyles = {
    root: css({
        padding: space[24],
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    }),
    callToAction: css({
        rowGap: space[8],
        padding: space[48],
        alignSelf: "stretch",
        justifyContent: "center",
        borderRadius: borderRadius.small,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: themeTokens.color.background.secondary.default,
        color: themeTokens.color.text.secondary,
    }),
};
const stickyColumn = css({
    position: "sticky",
    left: 0,
    backgroundColor: themeTokens.color.background.primary.default,
    zIndex: 1,
});
const lastRowHeaderStyle = css({
    borderRadius: borderRadius.medium,
});
const tableStyle = css({
    borderSpacing: "unset",
});
function getDeploymentTenantProgress(value: ProjectsPageEnvironmentDeployment | EnvironmentWithDeployments): TenantDeploymentProgress | undefined {
    return "TenantDeploymentProgress" in value ? value.TenantDeploymentProgress : undefined;
}
const deploymentsTableContainerStyle = css({
    width: "100%",
    overflowX: "auto",
    isolation: "isolate",
});
const topScrollbarMirrorStyles = css({
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
});
const disabledProjectRowStyles = css({
    opacity: 0.4,
});
export const deploymentsTableColumnHeaderStyles = css({
    height: "2.25rem",
});
const deploymentStatusCellCollapsedStyles = css({
    minWidth: "104px",
    maxWidth: "104px",
});
const tableCellStyles = css({
    padding: 0,
});
const tableCellExpandedStyles = css({
    minWidth: "14rem",
});
interface DeploymentStatusCellProps {
    children: React.ReactNode;
    isCollapsed: boolean;
}
function DeploymentStatusCell({ children, isCollapsed }: DeploymentStatusCellProps) {
    return (<td className={tableCellStyles}>
            <div className={cx(deploymentCellStyles, isCollapsed ? deploymentStatusCellCollapsedStyles : tableCellExpandedStyles)}>{children}</div>
        </td>);
}
export const deploymentCellStyles = css({
    display: "flex", // Needed to maintain vertically center alignment.
    padding: `${space[12]} ${space[6]}`,
});
interface ProjectGroupInitialDeploymentsTableProps {
    favouriteProjectsData: FavouriteProjectsData;
    group: InitialDashboardProjectGroup;
    onAddProjectToGroup: (projectGroupId: string) => void;
    projectStatus: ProjectStatus;
}
function ProjectGroupInitialDeploymentsTable({ favouriteProjectsData, group, onAddProjectToGroup, projectStatus }: ProjectGroupInitialDeploymentsTableProps) {
    if (group.TotalProjects === 0) {
        return <EmptyProjectGroup onAddProjectToGroup={() => onAddProjectToGroup(group.Id)}/>;
    }
    return (<div className={cx(deploymentsTableContainerStyle, scrollbarStyles)}>
            <table aria-label={group.Name} className={tableStyle}>
                <thead>
                    <tr className={environmentHeaderPlaceholderStyles}/>
                </thead>
                <tbody>
                    {group.Projects.map((project, index, arr) => (<tr key={index}>
                            <th scope={"row"} className={cx(tableCellStyles, tableCellExpandedStyles, stickyColumn, { [lastRowHeaderStyle]: index === arr.length - 1 })}>
                                <ProjectsDashboardRowHeaderContent favouriteProjectsData={favouriteProjectsData} project={project} projectStatus={projectStatus}/>
                            </th>
                            <ProjectDeploymentRowSkeleton key={project.Id}/>
                        </tr>))}
                </tbody>
            </table>
        </div>);
}
const environmentHeaderPlaceholderStyles = css({
    height: "2.25rem",
});
function ProjectDeploymentRowSkeleton() {
    return (<>
            {[...Array(4)].map((_, index) => (<DeploymentStatusCell key={index} isCollapsed={false}>
                    <div className={deploymentSkeletonStyles}>
                        <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                    </div>
                </DeploymentStatusCell>))}
        </>);
}
const deploymentSkeletonStyles = css({
    height: "1.25rem",
    width: "10.75rem",
    alignSelf: "center",
});
const noDataStyles = css({
    display: "flex",
    columnGap: space[6],
    font: text.body.bold.medium,
    color: themeTokens.color.text.disabled,
    alignItems: "center",
    padding: space[6],
});
interface NoDataProps {
    isCollapsed: boolean;
}
function NoData({ isCollapsed }: NoDataProps) {
    return (<div className={noDataStyles}>
            <UnavailableIcon size={24}/>
            {!isCollapsed && "No Data"}
        </div>);
}
interface EnvironmentColumnHeaderProps {
    name: string;
    isCollapsed: boolean;
    toggleCollapse: () => void;
}
const environmentColumnHeaderCollapseContainerStyles = css({
    display: "flex",
    alignItems: "center",
});
function EnvironmentColumnHeader({ name, toggleCollapse, isCollapsed }: EnvironmentColumnHeaderProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const isHovered = useIsHovered(containerRef);
    return (<th scope={"col"} className={tableCellStyles}>
            <div ref={containerRef} className={cx(environmentColumnHeaderStyles, deploymentsTableColumnHeaderStyles, isCollapsed ? environmentHeaderColumnHeaderCollapsedStyles : tableCellExpandedStyles)} title={name}>
                <div className={cx(environmentColumnHeaderNameStyles, collapsedEnvironmentColumnHeaderNameStyles)}>{name}</div>
                <div className={environmentColumnHeaderCollapseContainerStyles}>
                    <ColumnCollapseButton isCollapsed={isCollapsed} onToggle={toggleCollapse} isVisible={isHovered} columnLabel={name} showLabel={false}/>
                </div>
            </div>
        </th>);
}
function AllEnvironmentsColumnHeader({ name, toggleCollapse, isCollapsed }: EnvironmentColumnHeaderProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    return (<div ref={containerRef} className={cx(environmentColumnHeaderStyles, deploymentsTableColumnHeaderStyles, allEnvironmentHeaderColumnHeaderStyles)}>
            <div className={environmentColumnHeaderCollapseContainerStyles}>
                <HoverRevealedColumnCollapseButton containerRef={containerRef} isCollapsed={isCollapsed} onToggle={toggleCollapse} columnLabel={name} showLabel={true}/>
            </div>
        </div>);
}
// Owns hover-state subscription locally so it only re-renders this button,
// not the surrounding column header (which sits inside <thead> and would
// otherwise reconcile its <th> wrapping on every hover transition).
function HoverRevealedColumnCollapseButton({ containerRef, isCollapsed, onToggle, columnLabel, showLabel }: {
    containerRef: React.RefObject<HTMLElement>;
    isCollapsed: boolean;
    onToggle: () => void;
    columnLabel: string;
    showLabel: boolean;
}) {
    const isHovered = useIsHovered(containerRef);
    return <ColumnCollapseButton isCollapsed={isCollapsed} onToggle={onToggle} isVisible={isHovered} columnLabel={columnLabel} showLabel={showLabel}/>;
}
const environmentColumnHeaderNameStyles = css({
    flex: 1,
});
const collapsedEnvironmentColumnHeaderNameStyles = css({
    textOverflow: "ellipsis",
    overflow: "hidden",
});
interface ColumnCollapseButtonProps {
    isCollapsed: boolean;
    onToggle: () => void;
    isVisible: boolean;
    columnLabel: string;
    showLabel: boolean;
}
const columnCollapseButtonStyles = css({
    backgroundColor: "transparent",
    border: "none",
    color: "inherit",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    gap: space[4],
    alignItems: "center",
    justifyContent: "end",
    font: text.body.bold.xSmall,
    letterSpacing: letterSpacing.wider,
    textTransform: "uppercase",
});
const hiddenColumnCollapseButtonStyles = css({
    display: "none",
});
function ColumnCollapseButton({ isCollapsed, onToggle, isVisible, columnLabel, showLabel }: ColumnCollapseButtonProps) {
    return (<button className={cx(columnCollapseButtonStyles, isVisible ? false : hiddenColumnCollapseButtonStyles)} onClick={onToggle} aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${columnLabel}`}>
            {showLabel && columnLabel}
            {isCollapsed ? <ArrowRightToLineIcon size={16}/> : <ArrowLeftFromLineIcon size={16}/>}
        </button>);
}
const environmentColumnHeaderStyles = css({
    color: themeTokens.color.text.tertiary,
    font: text.body.bold.xSmall,
    letterSpacing: letterSpacing.wider,
    textTransform: "uppercase",
    textAlign: "start",
    paddingTop: space[16],
    paddingLeft: space[8],
    paddingRight: space[8],
    display: "flex",
    justifyContent: "end",
    "&:hover": {
        color: themeTokens.color.text.primary,
    },
});
const projectGroupLinkStyles = css({
    color: `${themeTokens.color.text.primary} !important`,
    "&:hover": {
        span: {
            color: themeTokens.color.text.link.default,
        },
    },
});
const allEnvironmentHeaderColumnHeaderStyles = css({
    textAlign: "end",
});
const environmentHeaderColumnHeaderCollapsedStyles = css({
    minWidth: "6.5rem",
    maxWidth: "6.5rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
});
const environmentCountIndicatorStyles = css({
    display: "flex",
    justifyContent: "end",
    padding: `${space[6]} ${space[16]}`,
    font: text.body.regular.small,
    color: themeTokens.color.text.tertiary,
});
const loadMoreColumnHeaderStyles = css({
    minWidth: "10rem",
    verticalAlign: "middle",
    paddingLeft: space[8],
    paddingRight: space[8],
});
